import { Box, Divider, Stack, Typography } from "@mui/material";
import { TOptions } from "i18next";
import { useTranslation, Trans } from "next-i18next";
import { IconInfoOuterCircle } from "src/components/icons-mui/info-outer-circle";
import { useRouterLocale } from "src/utils/router/use-router-locale";
import { CBDetailsAccordion } from "../components/details-accordion";
import { CBFooter } from "../components/footer";
import { CBHeader } from "../components/header/header";
import { InfoBox } from "../components/info-box";
import { CBInvestedCapital } from "../components/invested-capital";
import { CBPrices } from "../components/prices";
import { CBRatioTabBar } from "../components/ratio-tab-bar";
import { SpacedContainer } from "../components/spaced-container";
import { CBCertificateFee, CBEmission, CBLiquidationFigure, CBPerformanceFee, CBTradingVolume } from "../components/statistics";
import { useCertificateBoxContext } from "../context";
import { certificateRatio } from "../utils";

const CERTIFICATE_COUNTRY_AVAILABILITY_TEST_ID = "certificate-country-availability";
const MIN_CERTIFICATES_FOR_MULTI_LICENSE = 2;

const useCertificateTranslationOptions = () => {
    const {
        data: { certificates },
    } = useCertificateBoxContext();
    const { country } = useRouterLocale();
    const { t } = useTranslation(["wf-detail"]);

    const countryText = t(`countries.${country.toLowerCase()}`);
    const licensedCertificates = certificates.filter(cert => cert.isLicensed);

    const translationOptions: TOptions = {
        country: countryText,
        // create indices (used in wf-detail.json) with ratios of licensed certificates (e.g., {{0}}: "10:1", {{1}}: "100:1").
        ...licensedCertificates.reduce((acc, cert, i) => {
            acc[i] = certificateRatio(cert.exchangeRatioMultiplier);
            return acc;
        }, {} as Record<number, string>),
    };

    const areMultiLicensedCertificates = licensedCertificates.length >= MIN_CERTIFICATES_FOR_MULTI_LICENSE;

    return {
        translationOptions,
        areMultiLicensedCertificates,
    };
};

export const CertificateBoxNotLicensedSingle = () => {
    const { translationOptions, areMultiLicensedCertificates } = useCertificateTranslationOptions();

    return (
        <>
            <CBHeader />
            <SpacedContainer>
                <CBRatioTabBar />
                <CBPrices />
                <Divider />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <CBCertificateFee />
                    <CBPerformanceFee />
                    <CBInvestedCapital />
                </Box>

                <Divider />

                <CBDetailsAccordion>
                    <Stack sx={{ gap: 1 }}>
                        <CBEmission />
                        <CBLiquidationFigure />
                        <CBTradingVolume />
                    </Stack>
                    <Divider sx={{ pb: 2 }} />
                </CBDetailsAccordion>

                <InfoBox icon={<IconInfoOuterCircle sx={{ mt: 0.2, width: "18px", height: "18px" }} />}>
                    <Typography fontSize="sm" data-test-id={CERTIFICATE_COUNTRY_AVAILABILITY_TEST_ID}>
                        <Trans
                            i18nKey={`certificate.country-availability${areMultiLicensedCertificates ? "-multi" : ""}`}
                            ns="wf-detail"
                            values={{
                                ...translationOptions,
                            }}
                            components={{ strong: <strong /> }}
                        />
                    </Typography>
                </InfoBox>

                <CBFooter />
            </SpacedContainer>
        </>
    );
};
